import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import toast from 'react-hot-toast';
import TwoFactorVerification from '../components/TwoFactorVerification';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, loading } = useAuth();
  
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [tempToken, setTempToken] = useState(null);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) return setErrors(validationErrors);
    setIsSubmitting(true);
    try {
      const result = await login(formData.email, formData.password);
      
      // Check if 2FA is required
      if (result.requiresTwoFactor) {
        setTwoFactorRequired(true);
        setTempToken(result.tempToken);
        toast.success('Please enter your 2FA code');
        return;
      }
      
      if (result.success) {
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      } else {
        // Check for specific error codes
        if (result.errorCode === 'EMAIL_NOT_VERIFIED') {
          // Don't show toast here - AuthContext already shows it
          // Redirect to verify email page immediately
          navigate('/verify-email', { state: { email: formData.email } });
        } else if (result.errorCode === 'USER_NOT_FOUND') {
          setErrors({ 
            general: (
              <div>
                No account found with this email.{' '}
                <Link to="/register" className="underline hover:no-underline font-semibold">
                  Create an account
                </Link>
              </div>
            )
          });
        } else if (result.errorCode === 'INVALID_PASSWORD') {
          setErrors({ 
            password: 'Incorrect password. Please try again.',
            general: (
              <div>
                Forgot your password?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password', { state: { email: formData.email } })}
                  className="underline hover:no-underline font-semibold"
                >
                  Reset it here
                </button>
              </div>
            )
          });
        } else {
          setErrors({ general: result.error || 'Login failed. Please try again.' });
        }
      }
    } catch (err) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle successful 2FA verification
  const handle2FASuccess = (data) => {
    toast.success(`Welcome back, ${data.user.firstName || data.user.email}!`);
    const from = location.state?.from?.pathname || '/dashboard';
    navigate(from, { replace: true });
    // Reload to update auth context
    window.location.href = from;
  };
  
  // Handle 2FA cancel
  const handle2FACancel = () => {
    setTwoFactorRequired(false);
    setTempToken(null);
    setFormData({ email: '', password: '' });
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (user && !loading) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, location]);
  
  // Show 2FA verification if required
  if (twoFactorRequired && tempToken) {
    return (
      <TwoFactorVerification
        tempToken={tempToken}
        onSuccess={handle2FASuccess}
        onCancel={handle2FACancel}
      />
    );
  }

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{
        background: 'linear-gradient(135deg, #197560 0%, #04947A 50%, #0D738E 100%)'
      }}>
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{
      background: 'linear-gradient(135deg, #197560 0%, #04947A 50%, #0D738E 100%)'
    }}>
      {/* Header: full logo only, separated */}
      <header className="w-full py-4 px-6 bg-white/95 backdrop-blur border-b border-gray-100 shadow-sm">
        <div className="w-full max-w-7xl mx-auto">
          <a href="/" className="inline-block hover:opacity-80 transition-opacity cursor-pointer">
            <img
              src="https://xloud.tech/s3/.Xlogos/Xloud%20Tech%20logo%20high%20Res.svg"
              alt="Xloud Technologies"
              className="h-12 w-auto"
              onError={(e) => { e.currentTarget.src = '/logo.svg'; }}
            />
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="w-full py-10 px-6 text-center">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-5xl font-bold text-white mb-3">Customer Portal</h1>
          <p className="text-white/90">Access your license management dashboard</p>
        </div>
      </section>

      {/* Content container: left SVG, right form */}
      <section className="w-full px-6 pb-16">
        <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-2xl p-6 md:p-10">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            {/* Left: SVG */}
            <div className="flex items-center justify-center md:justify-start">
              <img
                src="/login-illustration.svg"
                alt="Sign in illustration"
                className="w-full max-w-lg h-auto"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>

            {/* Right: Form */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              {/* Error */}
              {errors.general && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-red-800">{errors.general}</span>
                  </div>
                </div>
              )}

              {/* Form */}
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <Label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Business Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="email@business"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`transition-all duration-200 focus:ring-2 focus:ring-opacity-50 ${
                      errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'focus:border-primary focus:ring-primary'
                    }`}
                  />
                  {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <Label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Password *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`pr-12 transition-all duration-200 focus:ring-2 focus:ring-opacity-50 ${
                        errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'focus:border-primary focus:ring-primary'
                      }`}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-primary transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L12 12m-2.122-2.122L7.757 7.757M9.878 9.878l4.242 4.242m0 0L12 12m2.121-2.122L16.243 7.757" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.543 7-1.275 4.057-5.065 7-9.543 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <p className="mt-2 text-sm text-red-600">{errors.password}</p>}
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    type="submit"
                    className="px-8 py-3 font-semibold text-white rounded-lg transition-all duration-200 transform hover:scale-[1.02] focus:scale-[1.02] shadow-lg hover:shadow-xl"
                    style={{ background: 'linear-gradient(135deg, #197560 0%, #04947A 50%, #0D738E 100%)' }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Signing in...' : 'Sign In'}
                  </Button>
                </div>

                <div className="text-center space-y-2 pt-4 border-t border-gray-100">
                  <div>
                    <Link to="/register" className="text-primary hover:text-primary-dark font-medium transition-colors hover:underline">
                      Create new account
                    </Link>
                  </div>
                  <div>
                    <Link to="/forgot-password" className="text-gray-600 hover:text-gray-700 text-sm transition-colors">
                      Forgot password?
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full py-4 px-6 border-t border-white/10 mt-auto">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-sm text-white/70">
            © 2025 Xloud Technologies Pvt Ltd. All rights reserved. | License Management System
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;